import React, { useCallback, useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { InfoCircleOutlined, PrinterOutlined, LoadingOutlined } from '@ant-design/icons';
import ShareButton from '@client/components/ShareButton';
import { useTranslation } from 'react-i18next';
import { HtmlContent } from '@client/utils/html_content';
import StepWizard from '../StepWizard';
import ResetButton from '../ResetButton';
import TutorialButton from '@client/components/ui/TutorialButton';
import { ProductLabelButton } from '@client/feature/riskCalculator/components/steps/components';
import melangeFullIcon from '../../../../../img/client/icons/melange_full.svg';
import melangeEmptyIcon from '../../../../../img/client/icons/melange_empty.svg';

/**
 * ResultsScreen Component
 * Displays spray calculation results
 */
const ResultsScreen = React.memo(
  ({
    results,
    selections,
    enrichedData,
    onReset,
    onGoToForm,
    isStepValid,
    onStepClick,
    currentStep,
  }) => {
    const { t } = useTranslation('sprayCalculator');
    const [pdfLoading, setPdfLoading] = useState(false);

    // Force view=results in the shared URL so the recipient lands on the
    // results page, not the step they last edited.
    const handleShare = useCallback(async () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('view', 'results');
        await navigator.clipboard.writeText(url.toString());
        message.success({
          content: t('common|Link copied to clipboard', 'Lien copié dans le presse-papiers'),
          duration: 10,
          closable: true,
        });
        return true;
      } catch (err) {
        console.error('Failed to copy share URL:', err);
        message.error(t('common|Failed to copy link', 'Échec de la copie du lien'));
        return false;
      }
    }, [t]);

    const handlePrint = useCallback(async () => {
      setPdfLoading(true);
      try {
        const csrfToken = document.cookie
          .split('; ')
          .find((row) => row.startsWith('csrftoken='))
          ?.split('=')[1];
        const response = await fetch('/api/sprays/calculate/pdf/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken && { 'X-CSRFToken': csrfToken }),
          },
          body: JSON.stringify({ results }),
        });
        if (!response.ok) throw new Error('PDF generation failed');
        const blob = await response.blob();
        const pdfUrl = window.URL.createObjectURL(blob);
        window.open(pdfUrl, '_blank');
      } catch (err) {
        console.error('PDF generation failed:', err);
        message.error(t('print_error', 'Erreur lors de la génération du PDF'));
      } finally {
        setPdfLoading(false);
      }
    }, [results, t]);

    if (!results) return null;

    const products = results.products || [];

    return (
      <div className="flex flex-col gap-6" style={{ fontFamily: 'Roboto, sans-serif' }}>
        {/* Header with title and reset */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="title-underlined normal-case m-0 text-black" style={{ fontSize: 30, fontWeight: 500, lineHeight: '40px' }}>
            {t('title')}
          </h2>
          <ResetButton onReset={onReset} />
        </div>

        {/* Summary info bar */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="font-semibold text-sm">{t('info_summary')}</span>
            <Tooltip title={t('info_modify_hint')}>
              <InfoCircleOutlined className="text-[#595959] cursor-help" />
            </Tooltip>
          </div>
          <StepWizard
            currentStep={4}
            onStepClick={(step) => {
              onGoToForm();
              onStepClick(step);
            }}
            isStepValid={isStepValid}
            selections={selections}
            enrichedData={enrichedData}
          />
        </div>

        {/* Results content */}
        <div className="mt-6">
          {/* Results header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
            <h3 className="title-underlined normal-case m-0 text-black" style={{ fontSize: 30, fontWeight: 500, lineHeight: '40px' }}>
              {t('results_title')}
            </h3>
            <div className="flex flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
              <TutorialButton
                slotKey="spray-calculator-results"
                className="w-full sm:w-auto h-10 sm:h-[50px] px-3 sm:px-6 text-sm sm:text-base"
              />
              <Button
                icon={pdfLoading ? <LoadingOutlined /> : <PrinterOutlined />}
                onClick={handlePrint}
                loading={pdfLoading}
                className="w-full sm:w-auto h-10 sm:h-[50px] px-3 sm:px-6 text-sm sm:text-base"
              >
                {t('results_print')}
              </Button>
              <ShareButton
                onShare={handleShare}
                className="w-full sm:w-auto h-10 sm:h-[50px] px-3 sm:px-6 text-sm sm:text-base"
              />
            </div>
          </div>

          {/* Section 1: Total mixture */}
          <div className="mb-8">
            <h4 className="title-underlined normal-case mb-4 text-black" style={{ fontSize: 24, fontWeight: 500, lineHeight: '32px' }}>
              {t('results_section_total_title')}
            </h4>

            <div className="flex flex-col gap-4">
              <ResultCard
                label={t('results_area_to_treat')}
                value={results.area_to_treat_display}
              />

              <ResultCard label={t('results_total_products')}>
                <ProductList products={products} displayKey="total_quantity_display" t={t} />
              </ResultCard>

              <ResultCard label={t('results_total_water')}>
                <HtmlContent html={results.total_water_volume_display} className="text-xs text-black font-normal leading-5" />
              </ResultCard>

              <ResultCard
                label={t('results_total_tanks')}
                value={results.total_tanks_display}
              />
            </div>
          </div>

          {/* Section 2: Per tank breakdown */}
          <div>
            <h4 className="title-underlined normal-case mb-4 text-black" style={{ fontSize: 24, fontWeight: 500, lineHeight: '32px' }}>
              {t('results_section_per_tank_title')}
            </h4>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Full tanks column */}
              {results.full_tanks_count > 0 && (
                <div className="flex flex-col gap-4">
                  <div className="bg-[#E9F1D9] rounded-[16px] px-4 py-3 flex items-center gap-3">
                    <img src={melangeFullIcon} alt="" className="w-8 h-8" />
                    <span className="font-bold text-[#105235]">
                      {t('results_full_tanks')} : {results.full_tanks_count}
                    </span>
                  </div>
                  <p className="text-xs text-[#595959] font-normal leading-5">
                    {t('results_per_full_tank')}
                  </p>

                  <ResultCard label={t('results_covered_area')}>
                    <HtmlContent html={results.area_per_full_tank_display} className="text-xs text-black font-normal leading-5" />
                  </ResultCard>

                  <ResultCard label={t('results_product_quantity')}>
                    <ProductList products={products} displayKey="quantity_per_full_tank_display" t={t} />
                  </ResultCard>

                  <ResultCard label={t('results_water_volume')}>
                    <HtmlContent html={results.water_per_full_tank_display} className="text-xs text-black font-normal leading-5" />
                  </ResultCard>
                </div>
              )}

              {/* Partial tank column */}
              {results.has_partial_tank && (
                <div className="flex flex-col gap-4">
                  <div className="bg-[#E9F1D9] rounded-[16px] px-4 py-3 flex items-center gap-3">
                    <img src={melangeEmptyIcon} alt="" className="w-8 h-8" />
                    <span className="font-bold text-[#105235]">
                      {t('results_partial_tank')} : 1 ({t('results_of')} #{results.partial_tank_number})
                    </span>
                  </div>
                  <p className="text-xs text-[#595959] font-normal leading-5">
                    {t('results_per_partial_tank')}
                  </p>

                  <ResultCard label={t('results_covered_area')}>
                    <HtmlContent html={results.area_per_partial_tank_display} className="text-xs text-black font-normal leading-5" />
                  </ResultCard>

                  <ResultCard label={t('results_product_quantity')}>
                    <ProductList products={products} displayKey="quantity_per_partial_tank_display" t={t} />
                  </ResultCard>

                  <ResultCard label={t('results_water_volume')}>
                    <HtmlContent html={results.water_per_partial_tank_display} className="text-xs text-black font-normal leading-5" />
                  </ResultCard>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

/**
 * ResultCard - Card following Base pattern (bg-gray-50 rounded-[16px] p-6)
 */
const ResultCard = ({ label, value, children }) => (
  <div className="bg-gray-50 rounded-[16px] p-6 w-full">
    <div className="text-[#5B8C00] font-bold mb-1 text-base">{label}</div>
    {value && <span className="text-xs text-black font-normal leading-5">{value}</span>}
    {children}
  </div>
);

/**
 * ProductList - Renders product quantities with clickable product name
 */
const ProductList = ({ products, displayKey, t }) => (
  <div className="flex flex-col gap-2">
    {products.map((product) => {
      const display = product[displayKey];
      if (!display) return null;
      // Extract quantity part (before " de ") from display string
      const deIndex = display.indexOf(' de ');
      const quantityPart = deIndex > -1 ? display.substring(0, deIndex) : display;
      return (
        <div key={product.product_id} className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-black font-normal leading-5">
            {quantityPart} {t('results_of')}{' '}
            <a
              href={`/produits-commerciaux/details/${product.product_id}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#105235] font-bold hover:underline"
            >
              {product.product_name}
            </a>
            {product.approval_number ? ` (${product.approval_number})` : ''}
          </span>
          <ProductLabelButton
            approvalNumber={product.approval_number}
            tooltip={t('treatment_product_label_tooltip')}
          />
        </div>
      );
    })}
  </div>
);

ResultsScreen.displayName = 'ResultsScreen';

export default ResultsScreen;